// ============================================================================
// The two actions, and the one question in front of them.
//
// ONE component set, used by three entry points: the equipment detail page, the
// rollup row, and the QR scan landing. A scan that lands in the app after
// sign-in is indistinguishable from a tap that started in the app — same RPCs,
// same wording, same events, only `source` differs. Two write paths for one
// action is how floor_machines ended up with 0 of 26 confirmation columns
// populated while the AM-report table's were 85% populated.
//
// THE RULE THIS ENCODES: confirming and correcting cost the same. Every
// documented failure of a frontline check-in system has the same shape — the
// expected answer is cheap and the true answer is expensive, so people give the
// expected answer. So Yes and No are the same size, neither is pre-selected,
// neither is auto-focused, and there is a "Not sure" escape, because a forced
// binary produces a guess and a guess in the database is worse than a blank.
// ============================================================================
import { useState } from 'react';
import { Check, Hash, MapPin, Wrench, X } from 'lucide-react';
import { useToast } from '../../shared/components';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';
import {
  confirmMachineLocation,
  moveMachine,
  moveMachineByName,
  renumberMachine,
  setMachineStatus,
  type AssetWriteSource,
} from './hooks/use-asset-location';
import { machineLocation, type MachineRow } from './hooks/use-fleet-data';
import { MACHINE_STATUSES, statusLabel, isBroken } from './machine-status';
import { DestinationPicker, KindChip, type Destination } from './destination-picker';
import {
  notFoundMessage,
  parseDestinationRefusal,
  type DestinationCandidate,
} from './destination-errors';

const sheetCls =
  'fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 p-0 sm:items-center sm:p-4';
const panelCls =
  'max-h-[92vh] w-full overflow-y-auto rounded-t-2xl border border-tcs-border bg-tcs-overlay p-5 shadow-lg sm:max-w-lg sm:rounded-2xl';

function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title} className={sheetCls} {...overlayClickClose(onClose)}>
      <div className={panelCls} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-tcs-text">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const bigBtn =
  'flex min-h-[3.25rem] flex-1 items-center justify-center gap-2 rounded-xl px-3 text-base font-semibold disabled:opacity-60';

// ---------------------------------------------------------------------------
// Move
// ---------------------------------------------------------------------------
export function MoveMachineSheet({
  machine,
  source = 'app',
  alsoMarkBroken = false,
  onClose,
  onSaved,
}: {
  machine: MachineRow;
  source?: AssetWriteSource;
  /** The haul-out case: a machine collected for repair is moved AND broken in
   *  one trip, so it should be one interaction. */
  alsoMarkBroken?: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { success, error } = useToast();
  const [picked, setPicked] = useState<Destination | null>(null);
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  /** Set when the database refused to guess between several places. Rendering
   *  these as buttons is the whole point of the refusal — silently taking the
   *  first would put a machine somewhere nobody chose. */
  const [ambiguous, setAmbiguous] = useState<{
    query: string;
    candidates: DestinationCandidate[];
  } | null>(null);

  const current = machineLocation(machine);
  const currentId = current.id;
  /** Two ways to answer: pick a row, or type words and press Move. Either
   *  produces a destination — the picked one goes by id, the typed one goes to
   *  the database to resolve. */
  const canSubmit = !!picked || typed.trim().length > 0;

  /** The haul-out case, shared by both submit paths. */
  async function markBrokenIfAsked() {
    if (alsoMarkBroken && !isBroken(machine.status)) {
      await setMachineStatus({
        machineId: machine.machine_id,
        status: 'out_for_repair',
        note: note.trim() || null,
        source,
      });
    }
  }

  /** The combobox path. It already knows the id, so it passes the id — into
   *  p_office_id for an office and p_facility_id for a facility. */
  async function moveTo(dest: Destination) {
    setSaving(true);
    setAmbiguous(null);
    try {
      const msg = await moveMachine({
        machineId: machine.machine_id,
        facilityId: dest.kind === 'facility' ? dest.id : null,
        officeId: dest.kind === 'office' ? dest.id : null,
        note: note.trim() || null,
        source,
      });
      await markBrokenIfAsked();
      success(msg);
      onSaved();
      onClose();
    } catch (e) {
      error(errorMessage(e, 'Could not move the machine.'));
    } finally {
      setSaving(false);
    }
  }

  async function submit() {
    if (picked) {
      await moveTo(picked);
      return;
    }
    const words = typed.trim();
    if (!words) {
      error('Pick where the machine actually is, or type its name.');
      return;
    }
    // Nothing was picked, so the words go to the database, which resolves ids,
    // names, regions, addresses and aliases — and REFUSES rather than guessing.
    setSaving(true);
    setAmbiguous(null);
    try {
      const msg = await moveMachineByName({
        machineId: machine.machine_id,
        destination: words,
        note: note.trim() || null,
        source,
      });
      await markBrokenIfAsked();
      success(msg);
      onSaved();
      onClose();
    } catch (e) {
      // ⛔ Read the RAW refusal first. shared/errors.ts rewords Postgres for
      // humans (TCSD-0110), which would strip the machine-readable prefix and
      // the candidate list with it.
      const refusal = parseDestinationRefusal(e);
      if (refusal?.code === 'ambiguous' && refusal.candidates.length > 0) {
        setAmbiguous({ query: refusal.query || words, candidates: refusal.candidates });
      } else if (refusal?.code === 'not_found') {
        error(notFoundMessage(refusal.query || words));
      } else {
        error(errorMessage(e, 'Could not move the machine.'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet title={alsoMarkBroken ? 'Move and report broken' : `Move ${machine.machine_id}`} onClose={onClose}>
      {current.kind !== 'unassigned' && (
        <p className="mb-3 text-sm text-tcs-text-secondary">
          Currently recorded at{' '}
          <span className="font-medium text-tcs-text">{current.display}</span>.
        </p>
      )}
      <DestinationPicker
        value={picked}
        onChange={(d) => {
          setPicked(d);
          setAmbiguous(null);
        }}
        onQueryChange={(text) => {
          setTyped(text);
          // Typing again abandons a previous selection and a previous
          // ambiguity — both describe words that are no longer on screen.
          setPicked(null);
          setAmbiguous(null);
        }}
        currentId={currentId}
        autoFocus
      />

      {ambiguous && (
        // ⛔ NEVER SILENTLY CHOOSE THE FIRST. The database refused precisely so
        // a person decides, and the candidates only exist in the refusal.
        <div className="mt-3 rounded-lg border border-st-warn-border bg-st-warn-bg p-3">
          <p className="text-[13px] font-semibold text-st-warn-fg-strong">
            “{ambiguous.query}” matches {ambiguous.candidates.length} places. Which one?
          </p>
          <div className="mt-2 space-y-1.5">
            {ambiguous.candidates.map((c) => (
              <button
                key={`${c.kind}-${c.id}`}
                type="button"
                disabled={saving}
                onClick={() => {
                  void moveTo({
                    kind: c.kind,
                    id: c.id,
                    name: c.name,
                    detail: null,
                    matched_on: null,
                    rank: null,
                  });
                }}
                className="flex w-full items-center gap-2.5 rounded-lg border border-tcs-border bg-tcs-raised px-3 py-2.5 text-left disabled:opacity-60"
              >
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-tcs-text">
                  {c.name}
                </span>
                <KindChip kind={c.kind} />
              </button>
            ))}
          </div>
        </div>
      )}

      <label className="mt-3 block">
        <span className="mb-1 block text-xs font-medium text-tcs-text-secondary">
          Note <span className="font-normal text-tcs-text-muted">(optional)</span>
        </span>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Anything worth knowing about the move"
          className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base text-tcs-text placeholder:text-tcs-text-muted focus:border-tcs-border-input focus:outline-none"
        />
      </label>
      <div className="mt-4 flex gap-2.5">
        <button
          type="button"
          onClick={onClose}
          className="min-h-[3.25rem] rounded-xl border border-tcs-border bg-tcs-raised px-4 text-base text-tcs-text-secondary"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            void submit();
          }}
          disabled={saving || !canSubmit}
          className={`${bigBtn} bg-tcs-inverse text-tcs-inverse-ink`}
        >
          <MapPin className="h-4 w-4" />
          {saving ? 'Saving…' : picked ? 'Move here' : canSubmit ? 'Move' : 'Pick a place'}
        </button>
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Status / report broken
// ---------------------------------------------------------------------------
export function MachineStatusSheet({
  machine,
  source = 'app',
  initialStatus,
  onClose,
  onSaved,
}: {
  machine: MachineRow;
  source?: AssetWriteSource;
  initialStatus?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { success, error } = useToast();
  const [status, setStatus] = useState(
    initialStatus ?? (isBroken(machine.status) ? 'in_service' : 'needs_repair'),
  );
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try {
      const msg = await setMachineStatus({
        machineId: machine.machine_id,
        status,
        note: note.trim() || null,
        source,
      });
      success(msg);
      onSaved();
      onClose();
    } catch (e) {
      error(errorMessage(e, 'Could not change the status.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet title={`${machine.machine_id} status`} onClose={onClose}>
      <p className="mb-3 text-sm text-tcs-text-secondary">
        Currently <span className="font-medium text-tcs-text">{statusLabel(machine.status)}</span>.
      </p>
      <div className="space-y-2">
        {MACHINE_STATUSES.filter((s) => s !== machine.status).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-base ${
              status === s
                ? 'border-tcs-border-input bg-tcs-accent-bg font-semibold text-tcs-text'
                : 'border-tcs-border bg-tcs-raised text-tcs-text-secondary'
            }`}
          >
            <span
              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                status === s ? 'border-tcs-inverse bg-tcs-inverse' : 'border-tcs-border-strong'
              }`}
            >
              {status === s && <Check className="h-2.5 w-2.5 text-tcs-inverse-ink" />}
            </span>
            {statusLabel(s)}
          </button>
        ))}
      </div>
      <label className="mt-3 block">
        <span className="mb-1 block text-xs font-medium text-tcs-text-secondary">
          What is wrong with it? <span className="font-normal text-tcs-text-muted">(optional)</span>
        </span>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. brush motor will not turn"
          className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base text-tcs-text placeholder:text-tcs-text-muted focus:border-tcs-border-input focus:outline-none"
        />
      </label>
      {isBroken(status) && (
        <p className="mt-3 rounded-lg border border-st-info-border bg-st-info-bg px-3 py-2 text-[12.5px] text-st-info-fg-strong">
          This sends the National Accounts Manager a direct message. Several people reporting the same machine on the same day
          still only sends one.
        </p>
      )}
      <div className="mt-4 flex gap-2.5">
        <button
          type="button"
          onClick={onClose}
          className="min-h-[3.25rem] rounded-xl border border-tcs-border bg-tcs-raised px-4 text-base text-tcs-text-secondary"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          className={`${bigBtn} ${
            isBroken(status)
              ? 'border border-st-danger-border bg-tcs-raised text-st-danger-fg-strong'
              : 'bg-tcs-inverse text-tcs-inverse-ink'
          }`}
        >
          <Wrench className="h-4 w-4" />
          {saving ? 'Saving…' : `Set ${statusLabel(status).toLowerCase()}`}
        </button>
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Renumber — the forced-number backstop (TCSD-0143)
//
// Not an everyday action and not drawn like one. Minting fills the lowest
// unused number by itself, so this exists for the one case the mint rule cannot
// answer: a printed tag is already on the machine and the DATABASE is what has
// to move. Leadership only, matching the RPC.
// ---------------------------------------------------------------------------
export function RenumberMachineSheet({
  machine,
  onClose,
  onSaved,
}: {
  machine: MachineRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { success, error } = useToast();
  const [newId, setNewId] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const canSubmit = newId.trim().length > 0 && reason.trim().length > 0;

  async function submit() {
    setSaving(true);
    try {
      const msg = await renumberMachine({
        machineId: machine.machine_id,
        newMachineId: newId.trim(),
        reason: reason.trim(),
      });
      success(msg);
      onSaved();
      onClose();
    } catch (e) {
      // The RPC's refusals are written to be read by a person and name the
      // offending id. Show them as they are.
      error(errorMessage(e, 'Could not renumber the machine.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet title={`Renumber ${machine.machine_id}`} onClose={onClose}>
      <p className="mb-3 text-sm text-tcs-text-secondary">
        Use this only when the printed tag has to win — the machine already
        carries a tag with a different number on it. Numbers are never re-issued,
        so the new number has to be one nothing else is using.
      </p>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-tcs-text-secondary">
          New number
        </span>
        <input
          value={newId}
          onChange={(e) => setNewId(e.target.value)}
          placeholder="FM-0031"
          autoFocus
          className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base text-tcs-text placeholder:text-tcs-text-muted focus:border-tcs-border-input focus:outline-none"
        />
      </label>
      <label className="mt-3 block">
        <span className="mb-1 block text-xs font-medium text-tcs-text-secondary">
          Why <span className="font-normal text-tcs-text-muted">(required)</span>
        </span>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. the tag on this machine reads FM-0031"
          className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base text-tcs-text placeholder:text-tcs-text-muted focus:border-tcs-border-input focus:outline-none"
        />
      </label>
      <p className="mt-2 text-[12px] text-tcs-text-muted">
        The reason is recorded on this machine&rsquo;s history, and its movement
        records follow it to the new number.
      </p>
      <div className="mt-4 flex gap-2.5">
        <button
          type="button"
          onClick={onClose}
          className="min-h-[3.25rem] rounded-xl border border-tcs-border bg-tcs-raised px-4 text-base text-tcs-text-secondary"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            void submit();
          }}
          disabled={saving || !canSubmit}
          className={`${bigBtn} bg-tcs-inverse text-tcs-inverse-ink`}
        >
          <Hash className="h-4 w-4" />
          {saving ? 'Saving…' : 'Renumber'}
        </button>
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// The two actions as a reusable pair, plus the confirm question.
// ---------------------------------------------------------------------------
export function MachineActions({
  machine,
  source = 'app',
  onDone,
  showConfirm = true,
}: {
  machine: MachineRow;
  source?: AssetWriteSource;
  onDone: () => void;
  /** The scan flow asks the confirm question on its own screen, so it hides
   *  this one rather than asking twice. */
  showConfirm?: boolean;
}) {
  const { success, error } = useToast();
  const [sheet, setSheet] = useState<'move' | 'status' | null>(null);
  const [busy, setBusy] = useState(false);

  async function confirm() {
    setBusy(true);
    try {
      const msg = await confirmMachineLocation(machine.machine_id, source);
      success(msg);
      onDone();
    } catch (e) {
      error(errorMessage(e, 'Could not record the confirmation.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="flex gap-2.5">
        <button
          type="button"
          onClick={() => setSheet('move')}
          className={`${bigBtn} bg-tcs-inverse text-tcs-inverse-ink`}
        >
          <MapPin className="h-4 w-4" />
          Move it
        </button>
        <button
          type="button"
          onClick={() => setSheet('status')}
          className={`${bigBtn} border border-st-danger-border bg-tcs-raised text-st-danger-fg-strong`}
        >
          <Wrench className="h-4 w-4" />
          {isBroken(machine.status) ? 'Update status' : 'Report broken'}
        </button>
      </div>
      {showConfirm && (
        <button
          type="button"
          onClick={confirm}
          disabled={busy}
          className="mt-2.5 flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl border border-tcs-border bg-tcs-raised text-sm font-medium text-tcs-text-secondary disabled:opacity-60"
        >
          <Check className="h-4 w-4" />
          {busy ? 'Recording…' : 'Confirm it is still here'}
        </button>
      )}

      {sheet === 'move' && (
        <MoveMachineSheet
          machine={machine}
          source={source}
          onClose={() => setSheet(null)}
          onSaved={onDone}
        />
      )}
      {sheet === 'status' && (
        <MachineStatusSheet
          machine={machine}
          source={source}
          onClose={() => setSheet(null)}
          onSaved={onDone}
        />
      )}
    </>
  );
}
